import { useCallback, useEffect, useRef } from 'react';
import { endpoints } from '@rossum/api-client';
import { ProcessingDuration as ProcessingDurationResponse } from '@rossum/api-client/annotations';
import { api } from '../../../lib/apiClient';
import { createAuthJSONHeaders } from '../../../lib/apiHelpers';
import convertKeys, { camelToSnake } from '../../../lib/keyConvertor';
import {
  ProcessingDuration,
  timeSpent,
} from '../../../timers/timeSpent/timeSpent';
import { EditDocumentMode } from '../../document-edit-core';

type UseSplitSessionTimersArgs = {
  annotationId: number;
  annotationUrl: string;
  mode: EditDocumentMode;
  enabled: boolean;
};

type SplitSessionTimers = { endEditTimer: () => void };

// The generated schema has every field optional (the API omits unset ones),
// while the `timeSpent` singleton stores a fully populated snapshot.
const toProcessingDuration = ({
  annotation: _annotation,
  durationLatestValidateStarted = null,
  durationLatestValidateUpdatedBeforeConfirm = null,
  timeSpentActive = 0,
  timeSpentOverall = 0,
  timeSpentEdit = 0,
  timeSpentEmails = 0,
  timeSpentBlockers = 0,
  timeSpentOpening = 0,
}: ProcessingDurationResponse): ProcessingDuration => ({
  durationLatestValidateStarted,
  durationLatestValidateUpdatedBeforeConfirm,
  timeSpentActive,
  timeSpentOverall,
  timeSpentEdit,
  timeSpentEmails,
  timeSpentBlockers,
  timeSpentOpening,
});

export const useSplitSessionTimers = ({
  annotationId,
  annotationUrl,
  mode,
  enabled,
}: UseSplitSessionTimersArgs): SplitSessionTimers => {
  const closedRef = useRef(false);

  const endEditTimer = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    timeSpent.closeEditMode(annotationId);
  }, [annotationId]);

  useEffect(() => {
    if (!enabled) return undefined;

    timeSpent.openEditMode();
    timeSpent.startAnnotation(annotationId);

    return () => {
      endEditTimer();
      timeSpent.stopAnnotation(annotationId);
    };
  }, [enabled, annotationId, endEditTimer]);

  const isParentAnnotation = mode.type === 'parent-annotation';

  // One-shot seeding of the `timeSpent` singleton, not view data: nothing ever
  // reads the response, so this is a plain request rather than a query.
  useEffect(() => {
    if (!enabled || !isParentAnnotation) return undefined;

    let cancelled = false;

    api
      .request(endpoints.annotations.getProcessingDuration(annotationId))
      .then(response => {
        if (cancelled) return;

        timeSpent.setAnnotationInitialProcessingDuration(
          annotationId,
          toProcessingDuration(response)
        );
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, isParentAnnotation, annotationId, annotationUrl]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handlePageHide = (event: Event) => {
      if ((event as PageTransitionEvent).persisted) return;

      endEditTimer();
      const result = timeSpent.stopAnnotation(annotationId);

      fetch(`${annotationUrl}/cancel`, {
        method: 'POST',
        headers: createAuthJSONHeaders(),
        keepalive: true,
        body: JSON.stringify(convertKeys(camelToSnake)(result)),
      });
    };

    window.addEventListener('pagehide', handlePageHide, { capture: true });

    return () => {
      window.removeEventListener('pagehide', handlePageHide, {
        capture: true,
      });
    };
  }, [enabled, annotationId, annotationUrl, endEditTimer]);

  return { endEditTimer };
};
